import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useError } from '../../contexts/ErrorContext';
import InventoryLayout from './InventoryLayout';
import PlatformText from '../PlatformText';
import PlatformButton from '../PlatformButton';
import SmartProductTable from './SmartProductTable';
import SmartProductModal from './SmartProductModal';
import InventoryBackButton from './InventoryBackButton';
import { useProducts } from './hooks/useProducts';
import { getPlatformClass } from '../../utils/platformUtils';
import './ProductsPage.css';

function ProductsPage() {
  const navigate = useNavigate();
  const platformClass = getPlatformClass();
  const isMobilePlatform = platformClass.includes('ios') || platformClass.includes('android') || platformClass.includes('pwa');
  const { showErrorToast } = useError();

  // Use Firebase hooks
  const {
    products,
    loading: isLoading,
    error,
    canManageProducts,
    addProduct,
    updateProduct,
    deleteProduct,
    searchProducts,
    getCategories,
    clearError
  } = useProducts();

  // Local state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Get categories from existing products
  const categories = getCategories();

  // Filter products based on search and category
  const filteredProducts = searchProducts(searchTerm).filter(product => {
    const matchesCategory = selectedCategory === '' || selectedCategory === 'All Categories' || 
                           product.category === selectedCategory;
    return matchesCategory;
  });

  // Handle product operations
  const handleAddProduct = () => {
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const handleDeleteProduct = async (productId) => {
    // Find the product to get its name for confirmation
    const product = products.find(p => p.id === productId);
    if (!product) {
      showErrorToast('Product not found', {
        variant: 'error'
      });
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${product.name}"?`)) {
      try {
        await deleteProduct(productId);
        showErrorToast(`Successfully deleted "${product.name}"`, {
          variant: 'info',
          duration: 3000
        });
      } catch (error) {
        showErrorToast(`Failed to delete product: ${error.message}`, {
          variant: 'error'
        });
      }
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (editingProduct) {
        // Update existing product
        await updateProduct(editingProduct.id, productData);
      } else {
        // Add new product
        await addProduct(productData);
      }
      setIsModalOpen(false);
      setEditingProduct(null);
      // Success message could be shown here
    } catch (error) {
      showErrorToast(`Failed to save product: ${error.message}`, {
        variant: 'error'
      });
    }
  };



  // Show error if any
  if (error) {
    return (
      <InventoryLayout>
        <div className={`inventory-container ${platformClass}`}>
                  <div className="inventory-error">
          <PlatformText variant="h3">Error Loading Products</PlatformText>
          <PlatformText variant="body">{error}</PlatformText>
          <PlatformButton onClick={clearError}>Retry</PlatformButton>
        </div>
        </div>
      </InventoryLayout>
    );
  }

  if (isLoading) {
    return (
      <InventoryLayout>
        <div className={`inventory-container ${platformClass}`}>
                  <div className="inventory-loading">
          <PlatformText variant="h3">Loading products...</PlatformText>
        </div>
        </div>
      </InventoryLayout>
    );
  }

  return (
    <InventoryLayout>
      <div className={`inventory-container ${platformClass}`}>
        {/* Mobile App Bar */}
        {isMobilePlatform && (
          <div className="mobile-app-bar inventory-mobile-app-bar">
            <InventoryBackButton onClick={() => navigate('/inventory')}>Products</InventoryBackButton>
            {canManageProducts && (
              <PlatformButton onClick={handleAddProduct} className="mobile-add-button">
                +
              </PlatformButton>
            )}
          </div>
        )}

        {/* Desktop Header */}
        {!isMobilePlatform && (
          <div className="inventory-page-header">
            <div>
              <InventoryBackButton onClick={() => navigate('/inventory')}>Products</InventoryBackButton>
              <PlatformText variant="body" className="inventory-page-subtitle">
                {canManageProducts ? 'Manage your product catalog' : 'View your product catalog'} ({products.length} products)
              </PlatformText>
            </div>
            {canManageProducts && (
              <div className="inventory-page-actions">
                <PlatformButton onClick={handleAddProduct}>
                  Add Product
                </PlatformButton>
              </div>
            )}
          </div>
        )}

        {/* Search and Filter */}
        <div className="products-filters">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-container">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="category-filter"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Products Table */}
        <div className="products-table-container">
          {filteredProducts.length === 0 ? (
            <div className="products-empty">
              <PlatformText variant="h3">No products found</PlatformText>
              <PlatformText variant="body" className="empty-subtitle">
                {searchTerm || selectedCategory ? 'Try adjusting your search or filters.' : 'Get started by adding your first product.'}
              </PlatformText>
              {!searchTerm && !selectedCategory && canManageProducts && (
                <PlatformButton onClick={handleAddProduct}>
                  Add Your First Product
                </PlatformButton>
              )}
            </div>
          ) : (
            <SmartProductTable
              products={filteredProducts}
              onEdit={canManageProducts ? handleEditProduct : undefined}
              onDelete={canManageProducts ? handleDeleteProduct : undefined}
            />
          )}
        </div>

        {/* Smart Product Modal */}
        {canManageProducts && (
          <SmartProductModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setEditingProduct(null);
            }}
            onSave={handleSaveProduct}
            product={editingProduct}
            categories={categories}
          />
        )}
      </div>
    </InventoryLayout>
  );
}

export default ProductsPage; 